import type { ReactNode } from "react";
import { AlertTriangle, Inbox } from "lucide-react";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * C1 — the unified render-state language. Every data surface keeps the B5b
 * three-state contract (loading / error / legitimate-empty / data) but now
 * speaks it with ONE visual vocabulary instead of 15 ad hoc "Loading…" lines:
 *
 *  - {@link LoadingState} — skeleton shimmer (sr-only label for a11y; the
 *    pulse is disabled under prefers-reduced-motion).
 *  - {@link ErrorState}   — red, icon, role="alert": the request FAILED and
 *    what is on screen cannot be trusted.
 *  - {@link EmptyState}   — muted, icon: the request SUCCEEDED and there is
 *    legitimately nothing — §25: never collapsed with the error shape.
 *
 * All take a {@code testId} passthrough so the existing test contracts
 * (dq-error, params-empty, …) survive unchanged.
 */

export function LoadingState({
  lines = 2,
  label = "Chargement…",
  className,
  testId,
}: {
  /** Number of shimmer lines (approximate the content it replaces). */
  lines?: number;
  /** Screen-reader label (visually the shimmer says it). */
  label?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      className={cn("flex w-full flex-col gap-2", className)}
      data-testid={testId}
      aria-busy="true"
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            "h-4 motion-reduce:animate-none",
            i === lines - 1 && lines > 1 ? "w-2/3" : "w-full",
          )}
        />
      ))}
    </div>
  );
}

export function ErrorState({
  title,
  children,
  className,
  testId,
}: {
  /** Short bold headline ("Heatmap unavailable"). */
  title: ReactNode;
  /** Muted detail line(s) — what it means / what to do. */
  children?: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      className={cn("text-loss flex items-start gap-2 text-sm", className)}
      role="alert"
      data-testid={testId}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <strong>{title}</strong>
        {children != null && <p className="text-muted-foreground m-0">{children}</p>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  children,
  className,
  testId,
}: {
  /** Optional leading icon; defaults to a muted inbox. */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <p
      className={cn("text-muted-foreground flex items-start gap-2 text-sm", className)}
      data-testid={testId}
    >
      {icon ?? <Inbox className="mt-0.5 size-4 shrink-0 opacity-70" aria-hidden />}
      <span className="min-w-0">{children}</span>
    </p>
  );
}
